import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box,
    CircularProgress,
    Divider,
    IconButton,
    InputAdornment,
    MenuItem,
    OutlinedInput,
} from "@mui/material";
import Select from "./select/select";
import Checkbox from "./checkbox/checkbox";
import Typography from "./typography/typography";
import { Close } from "./icons";
import { debounce, sortBy } from "lodash";
import { fetchApi } from "../utils/api";
import ChipOrPlaceholder from "./form-control/form-builder/form-builder-element/chip-or-placeholder";
import { Add } from "@mui/icons-material";
import { useLanguage } from '@erpsquad/common/runtime';
import { useTranslation } from "react-i18next";
import { dynamicSelectAdd } from "./form-control/form-builder/form-builder-element/select-configuration";
import { useAuth } from '@erpsquad/common/runtime';

interface IOption {
    id: string | number;
    value: string | number;
    label: string | React.ReactNode;
    disabled?: boolean;
    isGroup?: boolean;
    options?: IOption[];
    searchLabel?: string;
}

interface IPaginationState {
    page: number;
    hasMore: boolean;
    isLoadingMore: boolean;
    total: number;
}

interface IMenuItems {
    options: IOption[];
    multiple: boolean;
    placeholder: string;
    with_checkboxes: boolean;
    values: string | string[];
    field: any;
    disabledIds: any[];
    handleClear: () => void;
    handleChange?: (value: string | string[]) => void;
    hasParent?: boolean;
}

// Custom API fetch function type for parent components
interface CustomFetchApiParams {
    search?: string;
    filters?: any;
    limit: number;
    skip: number;
    select?: string;
    order?: string;
}

interface CustomFetchApiResponse {
    data: IOption[];
    pagination?: {
        totalCount: number;
    };
}

interface SearchableSelectProps {
    labelId: string;
    label: string;
    value: any;
    onChange?: (event: any, selectedData?: any) => void;
    options: IOption[];
    multiple?: boolean;
    placeholder?: string;
    size?: "small" | "medium";
    searchPlaceholder?: string;
    CustomDropDownIcon: React.ReactNode;
    customStyle: any;
    name?: string;
    defaultValue?: any;
    apiType?: string;
    isInternal?: boolean;
    autoFocus?: any;
    error?: any;
    disabled: any;
    customeFilter?: string;
    attributes?: string;
    getSelectedData?: (row: any) => void;
    disabledIds: any[];
    labelKey?: string;
    valueKey?: string;
    inputStyle?: any;
    onChipRemove?: (value: string) => void;
    selectedLabel?: string;
    is_loading?: boolean;
    hasParent?: boolean;
    sortOrder?: string;
    handleChange?: (value: string | string[]) => void;
    enable_footer?: boolean;
    onFooterClick?: () => void;
    showCancelButton?: boolean;
    /** Custom API function to fetch options from parent component */
    customFetchApi?: (params: CustomFetchApiParams) => Promise<CustomFetchApiResponse>;
    addType?: string;
    isMergeOptions?: boolean;
    attributes?: string[];
    modalComponentProps?: any;
}

const generateMenuItems = ({
    options,
    multiple,
    placeholder,
    with_checkboxes,
    values,
    field,
    disabledIds,
    handleClear,
    handleChange,
    hasParent = false,
}: IMenuItems) => {
    const itemDisabled = (ls: any) => disabledIds?.includes(ls);
    const items: React.ReactElement[] = [];

    if (!multiple) {
        items.push(
            <MenuItem disabled value='' key="placeholder" sx={{ opacity: `0 !important`, height: `0 !important`, p: `0 !important`, m: `0 !important` }}>
                <Typography type='s3' color='theme.secondary.1000'>
                    <em>{placeholder}</em>
                </Typography>
            </MenuItem>
        );
    }

    const selectedValues = Array.isArray(values) ? values?.filter((value) => value) : [];

    // Parent selection handlers
    const handleParentChange = (parentValue: string, childOptions: IOption[]) => {
        const childValues = childOptions.map((child) => child.value);
        const valueArray = Array.isArray(selectedValues) ? selectedValues : [selectedValues];

        const isParentSelected = valueArray.includes(parentValue);
        const updatedValues = isParentSelected
            ? valueArray.filter((value) => value !== parentValue && !childValues.includes(value))
            : [...new Set([...valueArray, parentValue, ...childValues])];

        handleChange?.(updatedValues);
    };

    const isAllOptionsSelected = (parentValue: string, childOptions: IOption[]) => {
        const childValues = childOptions.map((child) => child.value);
        const valueArray = Array.isArray(selectedValues) ? selectedValues : [selectedValues];
        return childValues.every((childValue) => valueArray.includes(childValue));
    };

    const isSomeOptionsSelected = (childOptions: IOption[]) => {
        const childValues = childOptions.map((child) => child.value);
        const valueArray = Array.isArray(selectedValues) ? selectedValues : [selectedValues];
        return childValues.some((childValue) => valueArray.includes(childValue));
    };

    Array.isArray(options) && options?.length > 0 && options?.forEach((option, index) => {
        items.push(
            <MenuItem
                value={option?.value}
                key={`${option?.value}-${index}`}
                disabled={Boolean(option?.disabled) || itemDisabled(option?.value) || (hasParent && !multiple)}
                className={option?.isGroup ? 'select--MenuItemHeader' : ''}
                onClick={() => {
                    if (values?.length == 1) {
                        handleClear();
                    }
                }}
            >
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {multiple && with_checkboxes && (
                        <Checkbox
                            checked={
                                hasParent
                                    ? isAllOptionsSelected(option.value, option.options || [])
                                    : Boolean(Array.isArray(values) && values?.indexOf(option?.value) > -1)
                            }
                            indeterminate={
                                hasParent && isSomeOptionsSelected(option.options || [])
                            }
                            onChange={() =>
                                hasParent
                                    ? handleParentChange(option.value, option.options || [])
                                    : handleChange?.(option.value)
                            }
                        />
                    )}
                    <Typography
                        type='s3'
                        color='theme.secondary.1000'
                        onClick={() => handleChange?.(option.value)}
                    >
                        {option?.label}
                    </Typography>
                </Box>
            </MenuItem>
        );

        // Handle child options for grouped items
        if (option?.isGroup && Array.isArray(option?.options)) {
            option.options.forEach((opt, index) => {
                items.push(
                    <MenuItem
                        id={`${option.value}-${opt.value} ${index}`}
                        value={opt.value}
                        disabled={Boolean(opt.disabled) || itemDisabled(opt.value)}
                        sx={{ pl: 5 }}
                        onClick={() => {
                            if (values?.length == 1) {
                                handleClear();
                            }
                        }}
                        key={`${option.value}-${opt.value} ${index}`}
                    >
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {multiple && with_checkboxes && (
                                <Checkbox
                                    checked={Boolean(Array.isArray(values) && values?.indexOf(opt.value) > -1)}
                                />
                            )}
                            <Typography
                                id={`${option.value}-${opt.value} ${index}`}
                                type='s3'
                                color='theme.secondary.1000'
                                onClick={() => handleChange?.(String(opt.value))}
                            >
                                {opt.label == "Payment Entries" ? "Payment and Collection Entries" : opt.label || opt.value}
                            </Typography>
                        </Box>
                    </MenuItem>
                );
            });
        }
    });

    // Footer item
    if (field.enable_footer || field.addType) {
        items.push(
            <Box key="footer" sx={{ position: 'sticky', bottom: 0, background: '#fff', zIndex: 1 }}>
                <Divider flexItem sx={{ mt: '0.25rem !important', mb: '0.25rem !important' }} />
                <MenuItem sx={{ gap: 1 }} onClick={() => {
                    if (field.onFooterClick) {
                        field.onFooterClick();
                    } else if (field.handleModalOpen) {
                        field.handleModalOpen();
                    }
                }}>
                    <Add fontSize='small' />
                    <Typography type='s4' color='theme.secondary.1000'>
                        Create New {field.label}
                    </Typography>
                </MenuItem>
            </Box>
        );
    }

    // No data fallback
    if (!items.length || (items.length == 1 && !multiple)) {
        items.push(
            <MenuItem disabled key="no-data">
                No data available
            </MenuItem>
        );
    }

    return items;
};

const SearchableSelect: React.FC<SearchableSelectProps> = (props) => {
    const {
        labelId,
        label,
        value,
        onChange,
        options,
        multiple = false,
        size = "small",
        searchPlaceholder,
        placeholder,
        name,
        defaultValue,
        apiType,
        isInternal = false,
        autoFocus,
        error,
        disabled,
        customeFilter,
        attributes,
        getSelectedData,
        disabledIds,
        onChipRemove,
        selectedLabel,
        is_loading,
        customStyle = {},
        CustomDropDownIcon,
        labelKey,
        valueKey,
        inputStyle,
        hasParent = false,
        sortOrder = 'id:-1',
        handleChange,
        enable_footer = false,
        onFooterClick,
        showCancelButton = true,
        customFetchApi,
        addType,
        isMergeOptions = false,
    } = props;

    const [searchQuery, setSearchQuery] = useState("");
    const [loading, setLoading] = useState<boolean>(false);
    const [dynamicOptions, setOptions] = useState<IOption[]>([]);
    const [selectedOptions, setSelectedOptions] = useState<IOption[]>([]);
    const [isAlreadyLoaded, setIsAlreadyLoaded] = useState(false);
    const [initailLoading, setInitialLoading] = useState(false);
    const [searchInitiate, setSearchInitiate] = useState(false);
    const [open, setOpen] = useState(false);
    const [prevFilter, setPrevFilter] = useState<any>(null);
    const [addConfig, setAddConfig] = useState<any>(null);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [isAddLoading, setIsAddLoading] = useState<boolean>(false);
    const { t } = useTranslation();

    // Pagination state
    const [pagination, setPagination] = useState<IPaginationState>({
        page: 1,
        hasMore: true,
        isLoadingMore: false,
        total: 0
    });

    const { isRtl } = useLanguage();
    const limit = 25;

    const isInternalOptions = useMemo(() => {
        if (isMergeOptions && (apiType || customFetchApi)) return false;
        return isInternal || (!apiType && !customFetchApi);
    }, [isInternal, apiType, customFetchApi, isMergeOptions]);

    // Local search function for internal options
    const searchOptionsLocally = useCallback((searchTerm: string, value: any): IOption[] => {
        const currentOptions: IOption[] = (options && options.length > 0) ? options : [];
        if (!searchTerm) return currentOptions;

        const lowercasedSearchTerm = searchTerm.toLowerCase();

        const filteredOptions = hasParent
            ? options?.reduce((acc: IOption[], opt) => {
                const searchedOptions = opt.options?.filter((item) => {
                    const label = React.isValidElement(item.label) ? item.searchLabel : String(item.label);
                    return label?.toLowerCase().includes(lowercasedSearchTerm);
                });

                if (searchedOptions?.length) {
                    acc.push({ ...opt, options: searchedOptions });
                }

                return acc;
            }, []) || []
            : options?.filter((opt) => {
                const label = React.isValidElement(opt.label) ? opt.searchLabel : String(opt.label);
                return label?.toLowerCase().includes(lowercasedSearchTerm);
            }) || [];

        return sortBySelectedValues(filteredOptions, value);
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options, hasParent]);

    // Clear Selected Options
    const handleClear = () => {
        onChange?.({ target: { value: multiple ? [] : null } });
        if (getSelectedData && typeof getSelectedData === "function") {
            getSelectedData(null);
        }
    };

    // Get unique options
    const getUniqueOptions = useCallback((data: IOption[]): IOption[] => {
        const uniqueOptions = [
            ...new Map(data?.map(item => [item.value, item])).values()
        ];
        return uniqueOptions;
    }, []);

    // Sort by selected values
    const sortBySelectedValues = useCallback((arrayToSort: IOption[], value: any): IOption[] => {
        const selectedValues = Array.isArray(value) ? value : [value];
        const sortedValue = sortBy(arrayToSort, (item) => {
            const index = selectedValues.indexOf(item.id || item.value);
            return index === -1 ? Infinity : index;
        });
        return sortedValue;
    }, []);

    // Options with search and pagination support
    const filteredOptions = useMemo(() => {
        if (isInternalOptions) {
            return searchOptionsLocally(searchQuery, value);
        }
        const apiOptions = sortBySelectedValues(getUniqueOptions([...dynamicOptions, ...selectedOptions]), value);
        if (isMergeOptions) {
            const localOptions = searchOptionsLocally(searchQuery, value);
            return getUniqueOptions([...localOptions, ...apiOptions]);
        }
        return apiOptions;
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery, dynamicOptions, options, value, isInternalOptions, selectedOptions, searchOptionsLocally, sortBySelectedValues, getUniqueOptions, isMergeOptions]);

    // Handle scroll pagination
    const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
        const isBottom = scrollHeight - scrollTop <= clientHeight + 10;

        if (isBottom && pagination.hasMore && !pagination.isLoadingMore && !isInternalOptions) {
            const nextPage = pagination.page + 1;
            fetchOptions(searchQuery, customeFilter, nextPage, true);
        }
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.hasMore, pagination.isLoadingMore, pagination.page, isInternalOptions, searchQuery, JSON.stringify(customeFilter)]);

    // Update Options
    const updateOptions = useCallback((newOptions: IOption[], append = false) => {
        setOptions((prevOptions) => {
            const oldOptions = append ? prevOptions : selectedOptions;
            const uniqueOptions = getUniqueOptions([...oldOptions, ...newOptions]);
            return uniqueOptions;
        });
    }, [selectedOptions, getUniqueOptions]);

    // Fetch Options with pagination support
    const fetchOptions = useCallback(async (searchTerm: any = '', filters = customeFilter, page = 1, append = false) => {
        // Skip if neither apiType nor customFetchApi is provided
        if (!apiType && !customFetchApi) return [];

        setLoading(true);
        if (page > 1) {
            setPagination(prev => ({ ...prev, isLoadingMore: true }));
        }

        try {
            let data: IOption[] = [];
            let paginationResult: { totalCount?: number } = {};

            // Use custom API if provided, otherwise use default fetchApi
            if (customFetchApi) {
                const response = await customFetchApi({
                    search: searchTerm,
                    filters,
                    limit,
                    skip: (page - 1) * limit,
                    select: attributes,
                    order: sortOrder
                });
                data = response?.data || [];
                paginationResult = response?.pagination || {};
            } else {
                const response = await fetchApi({
                    apiKey: apiType!,
                    filters: {
                        limit,
                        skip: (page - 1) * limit,
                        search: searchTerm,
                        filters,
                        select: attributes,
                        order: sortOrder
                    },
                    labelKey,
                    valueKey
                });
                data = response?.data || [];
                paginationResult = response?.pagination || {};
            }

            if (data && Array.isArray(data)) {
                updateOptions(data, append);

                // Update pagination
                setPagination(prev => ({
                    ...prev,
                    page,
                    hasMore: (paginationResult?.totalCount || 0) > (page * limit),
                    total: paginationResult?.totalCount || 0,
                    isLoadingMore: false
                }));

                if (data?.length > 0) {
                    setSelectedData();
                }
            }

            return data || [];
        } catch (error) {
            console.error('Error fetching options:', error);
            setPagination(prev => ({ ...prev, isLoadingMore: false }));
            return [];
        } finally {
            setLoading(false);
        }
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiType, customFetchApi, attributes, sortOrder, limit, updateOptions, dynamicOptions]);

    // Search Options with debounce
    const debounceSearch = useMemo(
        () => debounce(async (searchTerm: string) => {
            if (loading) return;

            if (!searchTerm && !isInternalOptions && searchInitiate) {
                setPagination({
                    page: 1,
                    hasMore: true,
                    isLoadingMore: false,
                    total: 0
                });
                await fetchOptions('', customeFilter, 1, false);
            }
            if (searchTerm?.length > 1 && !isInternalOptions) {
                setPagination({
                    page: 1,
                    hasMore: true,
                    isLoadingMore: false,
                    total: 0
                });
                await fetchOptions(searchTerm, customeFilter, 1, false);
            }
        }, 700),
        //eslint-disable-next-line react-hooks/exhaustive-deps
        [isInternalOptions, searchInitiate, JSON.stringify(customeFilter)]
    );

    // Initial fetch with filter change detection
    const initialFetch = useCallback(async () => {
        const condition = JSON.stringify(customeFilter) !== JSON.stringify(prevFilter);

        if (condition && !isInternalOptions && !isAlreadyLoaded) {
            setPrevFilter(customeFilter);
            setOptions([]);
            setIsAlreadyLoaded(false);
            setPagination({
                page: 1,
                hasMore: true,
                isLoadingMore: false,
                total: 0
            });
        }

        if (!isInternalOptions && !isAlreadyLoaded) {
            await fetchOptions(searchQuery, customeFilter, 1, false);
            setIsAlreadyLoaded(true);
        }
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(customeFilter), isInternalOptions, isAlreadyLoaded]);

    // Set Selected Data
    const setSelectedData = useCallback(() => {
        let hasOptions = false;
        const isInOptions = filteredOptions?.filter((i: any) =>
            multiple ? value?.includes(i?.value) : i?.value == value
        );

        if ((Array.isArray(isInOptions) && isInOptions?.length > 0)) {
            getSelectedData?.(multiple ? isInOptions : isInOptions[0]);
            hasOptions = true;
        }

        return hasOptions;
    }, [JSON.stringify(filteredOptions), value, multiple]);

    // Fetch Options for Selected Id
    const fetchIdOptions = useCallback(async () => {
        if (await setSelectedData()) return;
        if (open) return;

        const fValue = multiple ? value && value?.filter(v => Boolean(Number(v))) : value ? value : null;
        const isNumericId = (v: any) =>
            v !== null && v !== undefined && v !== '' &&
            (typeof v === 'number' || (typeof v === 'string' && !isNaN(Number(v))));
        const condition = (multiple ? fValue?.length > 0 : isNumericId(fValue)) && !isInternalOptions;

        if (condition) {
            setInitialLoading(true);
            await fetchOptions('', { ...customeFilter, ...{ '&id.in': fValue } }, 1, false);
            setInitialLoading(false);
        }
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, value, JSON.stringify(customeFilter)]);

    const hasValue = multiple
        ? Array.isArray(value) && value.length > 0
        : value !== null && value !== undefined && value !== "";

    // Effects
    useEffect(() => {
        if (open) {
            debounceSearch(searchQuery);
        }
        return () => debounceSearch.cancel();
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery, JSON.stringify(customeFilter), open, isAlreadyLoaded, debounceSearch]);

    useEffect(() => {
        setSelectedData();
    }, [setSelectedData]);

    useEffect(() => {
        fetchIdOptions();
    }, [fetchIdOptions]);

    useEffect(() => {
        if (open) {
            initialFetch();
        }
    }, [open, initialFetch]);

    useEffect(() => {
        const condition = JSON.stringify(customeFilter) !== JSON.stringify(prevFilter);
        if (customeFilter && condition) {
            setIsAlreadyLoaded(false);
        }
    }, [customeFilter, prevFilter]);

    useEffect(() => {
        if (value && !open) {
            const currentOptions: IOption[] = (options && options.length > 0) ? options : [];
            const selectedData = currentOptions?.filter((i: any) =>
                multiple ? value?.includes(i?.value) : i?.value == value
            ) || [];
            setSelectedOptions(selectedData);
        }
        setSelectedOptions(filteredOptions?.filter((i: any) =>
            multiple ? value?.includes(i?.value) : i?.value == value
        ));
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, open, options, multiple]);

    const handleModalOpen = useCallback(() => {
        setIsModalOpen(true);
    }, []);

    // Handle modal save
    const handleModalSave = useCallback(async (payload: any) => {
        if (!addConfig || !addConfig.api) {
            console.error('Add configuration is not properly set');
            return;
        }

        setIsAddLoading(true);
        try {
            const result = await addConfig.api(payload);
            setIsAddLoading(false);
            setIsModalOpen(false);
            if (!isInternalOptions) {
                setPagination({
                    page: 1,
                    hasMore: true,
                    isLoadingMore: false,
                    total: 0
                });
                setIsAlreadyLoaded(false);
                await fetchOptions('', customeFilter, 1, false);
            }
            if (result && result.data) {
                const newValue = multiple
                    ? [...(value || []), result.data.id]
                    : result.data.id;
            }
        } catch (error) {
            console.log("error", error);
            setIsAddLoading(false);
        }


    }, [addConfig, isInternalOptions, fetchOptions, customeFilter, multiple, name, value]);

    useEffect(() => {
        const config = dynamicSelectAdd(addType!);
        setAddConfig(config);
    }, [addType])

    const handleOnChange = (event: any) => {
        const { value } = event.target;
        if ((multiple && Array.isArray(value) && value?.filter(v => v).length == 0) || !value) return;

        const isSelected = (item: any) => {
            return multiple ? value?.includes(item?.value) : item?.value === value;
        };

        const selected = filteredOptions?.filter(isSelected);
        onChange?.(event, multiple ? selected : selected?.[0]);
        getSelectedData?.(multiple ? selected : selected?.[0]);

    };

    const removeUndefine = (selectOptions: any[]) => {
        return Array.isArray(selectOptions) && selectOptions?.length > 0 && selectOptions?.filter((option) => option);
    };

    const showGroupLabel = (item: any) => {
        if (!item?.length) {
            return undefined
        }
        const mergedOptions = item?.flatMap((i: any) => i?.options)
        return mergedOptions?.find((option: any) => option?.value === value)?.label
    }

    return (
        <Select
            labelId={labelId}
            name={name}
            defaultValue={defaultValue || (multiple ? [] : '')}
            placeholder={t(placeholder)}
            className="filter-select"
            size={size}
            onOpen={() => setOpen(true)}
            onClose={() => {
                setOpen(false);
                if (searchQuery?.length > 0) {
                    setIsAlreadyLoaded(false);
                }
                setSearchInitiate(false);
                setSearchQuery('');
            }}
            onChange={handleOnChange}
            showCancelButton={showCancelButton}
            multiple={multiple}
            label={t(label)}
            fullWidth
            displayEmpty
            autoFocus={autoFocus}
            error={Boolean(error)}
            disabled={disabled}
            value={initailLoading ? (multiple ? [] : '') : value?.id || value || (multiple ? [] : '')}
            searchPlaceholder={searchPlaceholder}
            CustomDropdownIcon={CustomDropDownIcon}
            sx={(multiple && value?.length) ? { ...customStyle, "& .MuiSelect-select": { padding: '5.5px !important' } } : customStyle}
            MenuProps={{
                PaperProps: {
                    onScroll: handleScroll,
                    style: { maxHeight: 295, maxWidth: 250 }
                }
            }}
            input={
                !disabled ? (
                    <OutlinedInput
                        label=""
                        sx={inputStyle}
                        endAdornment={
                            <InputAdornment position="end">
                                {loading || is_loading ? (
                                    <CircularProgress
                                        size={20}
                                        sx={{ mr: 3, color: "#4AC08C" }}
                                    />
                                ) : (
                                    showCancelButton && hasValue && (
                                        <IconButton
                                            aria-label="clear selection"
                                            onClick={handleClear}
                                            edge="end"
                                            sx={isRtl ? { ml: 2.5 } : { mr: 1.5 }}
                                            size="small"
                                        >
                                            <Close fontSize="small" color='theme.primary.800' />
                                        </IconButton>
                                    )
                                )}
                            </InputAdornment>
                        }
                    />
                ) : undefined
            }
            renderValue={(selected?: string | string[]) => {
                return (
                    <>
                        {Array.isArray(selected) ? (
                            <ChipOrPlaceholder
                                selectedLabel={selectedLabel}
                                data={removeUndefine(selected)}
                                placeholder={placeholder}
                                onDelete={onChipRemove}
                                disabled={disabled}
                                chipProps={{
                                    sx: {
                                        bgcolor: 'theme.primary.100',
                                        border: 0,
                                        // borderRadius: '15px',
                                        '& .MuiTypography-body1': {
                                            color: 'theme.primary.800',
                                            fontWeight: "500",
                                        }
                                    }
                                }}
                            />
                        ) : (
                            hasParent ? showGroupLabel(filteredOptions) :
                                Array.isArray(filteredOptions) && filteredOptions?.length > 0 && filteredOptions?.find(
                                    (option: any) => option?.value == value
                                )?.label || selected || placeholder
                        )}
                    </>
                );
            }}
            handleSearch={(text) => {
                setSearchQuery(text);
                setSearchInitiate(true);
            }}
        >
            {generateMenuItems({
                options: filteredOptions,
                multiple: Boolean(multiple),
                placeholder,
                values: value,
                with_checkboxes: true,
                field: {
                    ...props,
                    enable_footer,
                    onFooterClick,
                    label,
                    handleModalOpen
                },
                disabledIds,
                handleClear,
                handleChange,
                hasParent,
                t: (key: string) => key
            })}
            {pagination.isLoadingMore && (
                <MenuItem disabled>
                    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                        <CircularProgress size={20} />
                    </Box>
                </MenuItem>
            )}
            {/* Modal for adding new items */}
            {addConfig && addConfig.component && (
                <>
                    {React.createElement(addConfig.component, {
                        isOpen: isModalOpen,
                        setIsOpen: setIsModalOpen,
                        isLoading: isAddLoading,
                        onSave: handleModalSave,
                        modalProps: {
                            maxWidth: 'md',
                            fullWidth: true
                        },
                        ...props?.modalComponentProps
                    })}
                </>
            )}
        </Select>
    );
};

export default SearchableSelect;